import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Mail, User, ShoppingBag, Sparkles, X, Send, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PURPOSES = [
  { value: 'thank_you', label: 'Thank You / Loyalty' },
  { value: 'winback', label: 'Win-back (lapsed buyer)' },
  { value: 'new_product', label: 'New Product Announcement' },
  { value: 'restock', label: 'Restock Reminder' },
  { value: 'compliance', label: 'Compliance Reminder' },
  { value: 'custom', label: 'Custom (your instructions)' },
];

const AiEmailModal = ({ customer, onClose }) => {
  const [purpose, setPurpose] = useState('thank_you');
  const [customPrompt, setCustomPrompt] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/customers/generate-email`, {
        email: customer.email,
        name: customer.name,
        purpose,
        custom_prompt: customPrompt || null,
      }, { withCredentials: true });
      setSubject(data.subject || '');
      setBody(data.body || '');
      toast.success('Draft generated — review & edit before sending');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate draft');
    } finally {
      setGenerating(false);
    }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body are required'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/admin/customers/send-email`, {
        email: customer.email, subject, body,
      }, { withCredentials: true });
      toast.success(`Email sent to ${customer.email}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="ai-email-modal">
      <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#A1A7B5] hover:text-white" data-testid="ai-email-close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-[#00F0FF]" />
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>AI Email</h2>
        </div>
        <p className="text-[#A1A7B5] text-sm mb-5">To: <span className="text-white">{customer.name}</span> ({customer.email})</p>

        {/* Purpose */}
        <label className="block text-[#A1A7B5] text-xs mb-1">Purpose</label>
        <select
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          className="input-field w-full mb-4"
          data-testid="ai-email-purpose"
        >
          {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>

        {/* Custom instructions */}
        <label className="block text-[#A1A7B5] text-xs mb-1">Extra instructions (optional)</label>
        <textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          rows={2}
          placeholder="e.g. mention our new BPC-157 batch, keep it under 120 words"
          className="input-field w-full mb-4 resize-y"
          data-testid="ai-email-instructions"
        />

        <Button
          onClick={generate}
          disabled={generating}
          className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold mb-6 flex items-center gap-2"
          data-testid="ai-email-generate"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? 'Generating…' : (subject ? 'Regenerate' : 'Generate with AI')}
        </Button>

        {/* Draft (editable) */}
        <label className="block text-[#A1A7B5] text-xs mb-1">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject line"
          className="input-field w-full mb-4"
          data-testid="ai-email-subject"
        />
        <label className="block text-[#A1A7B5] text-xs mb-1">Body (editable — sent with RENEW LABS branding)</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          placeholder="Generate a draft, or write your own. Separate paragraphs with a blank line."
          className="input-field w-full mb-5 resize-y font-[inherit]"
          data-testid="ai-email-body"
        />

        <div className="flex justify-end gap-3">
          <Button onClick={onClose} className="btn-secondary" data-testid="ai-email-cancel">Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || !subject.trim() || !body.trim()}
            className="bg-green-500 hover:bg-green-600 text-black font-semibold flex items-center gap-2"
            data-testid="ai-email-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending…' : 'Send Email'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [emailCustomer, setEmailCustomer] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/customers`, { withCredentials: true });
        setCustomers(data);
      } catch (err) {
        toast.error('Failed to load customers');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const filtered = customers.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || c.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-customers-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Customers
          </h1>
          <span className="text-[#A1A7B5]">{customers.length} total</span>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="input-field w-full pl-10"
              data-testid="customer-search"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
          </div>
          <div className="flex gap-2">
            {['all', 'registered', 'guest'].map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${
                  typeFilter === t
                    ? 'bg-[#00F0FF] text-black font-medium'
                    : 'bg-[#111621] text-[#A1A7B5] hover:bg-[#1E2532]'
                }`}
                data-testid={`filter-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Customers Table */}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1E2532]">
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Customer</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Email</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Type</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Orders</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Total Spent</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Joined</th>
                  <th className="text-right text-[#A1A7B5] text-sm font-medium p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-[#A1A7B5]">
                      No customers found
                    </td>
                  </tr>
                ) : (
                  filtered.map((customer) => (
                    <tr key={customer.id} className="border-b border-[#1E2532]/50 hover:bg-[#111621]/50" data-testid={`customer-row-${customer.id}`}>
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                            customer.type === 'registered' ? 'bg-[#00F0FF]/20' : 'bg-purple-500/20'
                          }`}>
                            <User className={`w-4 h-4 ${
                              customer.type === 'registered' ? 'text-[#00F0FF]' : 'text-purple-400'
                            }`} />
                          </div>
                          <span className="text-white font-medium text-sm">{customer.name}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-[#A1A7B5]" />
                          <span className="text-[#A1A7B5] text-sm">{customer.email}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          customer.type === 'registered'
                            ? 'bg-[#00F0FF]/10 text-[#00F0FF]'
                            : 'bg-purple-500/10 text-purple-400'
                        }`}>
                          {customer.type}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <ShoppingBag className="w-4 h-4 text-[#A1A7B5]" />
                          <span className="text-white text-sm">{customer.order_count}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-white font-semibold text-sm">
                          ${customer.total_spent.toFixed(2)}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-[#A1A7B5] text-sm">
                          {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => setEmailCustomer(customer)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition-colors"
                          data-testid={`ai-email-btn-${customer.id}`}
                        >
                          <Sparkles className="w-3.5 h-3.5" /> AI Email
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {emailCustomer && (
        <AiEmailModal customer={emailCustomer} onClose={() => setEmailCustomer(null)} />
      )}
    </div>
  );
};

export default AdminCustomersPage;
